import React, { useState, useEffect } from 'react';
import ModernTable, { ModernPageContainer, ModernPageHeader } from '../components/ModernTable';
import BackButton from '../../items/buttons/BackButton';
import LoadingIcon from '../../items/buttons/LoadingIcon';
import Swal from 'sweetalert2';
import request from '../../../services/Request';
import { userLimits } from '../uitls/userLimits';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Box,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import AddAudio from './AddAudio';

const getAudioURL = `/getAudioByEmail/`;

const deleteAudioURL = `/audio/delete/`;

export default function CustomAudio(props) {
  const theme = useTheme();
  const { _id, tableTitle, superAdmin, access } = userLimits(
    props.match.params.id,
    props.match.params.group,
    'Custom Audio',
    'edit_ss'
  );

  const [reflesh, setReflesh] = useState(false);
  const [audio, setAudio] = useState([]);
  const [loading, setLoading] = useState(false);

  const [show, setShow] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [row, setRow] = useState(null);

  const [pagination, setPagination] = useState(
    localStorage.getItem('table_view_audio') ? localStorage.getItem('table_view_audio') : 10
  );

  useEffect(() => {
    localStorage.setItem('table_view_audio', pagination);
  }, [pagination]);

  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    let isCancelled = false;

    async function getMyAudio() {
      await request({
        method: 'get',
        url: getAudioURL + _id,
      }).then((res) => {
        if (!isCancelled) {
          setAudio(res.data);
          setLoading(true);
        }
      });
    }

    getMyAudio();

    return () => {
      setAudio([]);
      isCancelled = true;
    };
  }, [reflesh]);

  const deleteAudio = async (id) => {
    setLoading(false);
    await request({
      method: 'delete',
      url: deleteAudioURL + id,
    })
      .then(() => {
        setLoading(true);
        reflesh ? setReflesh(false) : setReflesh(true);
      })
      .catch((err) => {
        if (err?.response?.status === 406) {
          Swal.fire({
            text: 'The audio is linked to some machines, please unlink it before delete',
            icon: 'error',
          });
        } else {
          Swal.fire({
            text: `Fail to delete the audio`,
            icon: 'error',
          });
        }

        setLoading(true);
        reflesh ? setReflesh(false) : setReflesh(true);
      });
  };

  const renderAudioStatus = (value) => {
    if (value && value !== '') {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          label="Yes"
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.success.main, 0.12),
            color: theme.palette.success.dark,
            border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
            '& .MuiChip-icon': {
              color: theme.palette.success.dark,
            },
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 16 }} />}
        label="No"
        size="small"
        sx={{
          fontWeight: 600,
          backgroundColor: alpha(theme.palette.grey[500], 0.12),
          color: theme.palette.text.secondary,
          border: `1.5px solid ${alpha(theme.palette.grey[500], 0.3)}`,
          '& .MuiChip-icon': {
            color: theme.palette.text.secondary,
          },
        }}
      />
    );
  };

  const columns = [
    {
      title: 'Audio Name',
      field: 'name',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          {rowData.name}
        </Typography>
      ),
    },
    {
      title: 'Touch Screen',
      field: 'start',
      render: (rowData) => renderAudioStatus(rowData.start),
    },
    {
      title: 'Shopping Cart',
      field: 'cart',
      render: (rowData) => renderAudioStatus(rowData.cart),
    },
    {
      title: 'Check Out',
      field: 'checkout',
      render: (rowData) => renderAudioStatus(rowData.checkout),
    },
    {
      title: 'Dispense',
      field: 'dispense',
      render: (rowData) => renderAudioStatus(rowData.dispense),
    },
    {
      title: 'Door Opens',
      field: 'doorOpen',
      render: (rowData) => renderAudioStatus(rowData.doorOpen),
    },
    {
      title: 'Door Closes',
      field: 'doorClose',
      render: (rowData) => renderAudioStatus(rowData.doorClose),
    },
  ];
  return (
    <ModernPageContainer>
      {!loading && <LoadingIcon />}

      <ModernPageHeader
        title={tableTitle || 'Custom Audio'}
        subtitle="Configure custom audio for different transaction stages"
        icon={AudiotrackIcon}
        buttonText={access ? "Add Audio" : undefined}
        buttonIcon={AddIcon}
        onButtonClick={access ? () => setShow(true) : undefined}
      />

      <Box sx={{ mt: 3 }}>
        <ModernTable
          onRowsPerPageChange={setPagination}
          columns={columns}
          data={audio}
          editable={
            access && {
              onRowDelete: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  setTimeout(() => {
                    deleteAudio(newData._id);
                    resolve();
                  }, 1000);
                }),
            }
          }
          detailPanel={({ rowData }) => {
            return (
              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.5)
                      : alpha(theme.palette.grey[100], 1),
                  p: 2,
                }}
              >
                {rowData.start && rowData.start !== '' && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '30%' }}>Audio</TableCell>
                        <TableCell style={{ width: '30%' }}>Section</TableCell>
                        <TableCell style={{ width: '30%' }}>Filename</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <audio controls>
                            <source
                              src={`${process.env.REACT_APP_SERVER_API}/videos/${rowData.start}/${_id}`}
                              type="video/mp4"
                            />
                          </audio>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          Touch the screen to start a transaction
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {rowData.start}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {rowData.cart && rowData.cart !== '' && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '30%' }}>Audio</TableCell>
                        <TableCell style={{ width: '30%' }}>Section</TableCell>
                        <TableCell style={{ width: '30%' }}>Filename</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <audio controls>
                            <source
                              src={`${process.env.REACT_APP_SERVER_API}/videos/${rowData.cart}/${_id}`}
                              type="video/mp4"
                            />
                          </audio>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          Open Shopping Cart
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {rowData.cart}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {rowData.checkout && rowData.checkout !== '' && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '30%' }}>Audio</TableCell>
                        <TableCell style={{ width: '30%' }}>Section</TableCell>
                        <TableCell style={{ width: '30%' }}>Filename</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <audio controls>
                            <source
                              src={`${process.env.REACT_APP_SERVER_API}/videos/${rowData.checkout}/${_id}`}
                              type="video/mp4"
                            />
                          </audio>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          Tap On Check out
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {rowData.checkout}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {rowData.dispense && rowData.dispense !== '' && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '30%' }}>Audio</TableCell>
                        <TableCell style={{ width: '30%' }}>Section</TableCell>
                        <TableCell style={{ width: '30%' }}>Filename</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <audio controls>
                            <source
                              src={`${process.env.REACT_APP_SERVER_API}/videos/${rowData.dispense}/${_id}`}
                              type="video/mp4"
                            />
                          </audio>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          While the machine dispenses
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {rowData.dispense}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {rowData.doorOpen && rowData.doorOpen !== '' && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '30%' }}>Audio</TableCell>
                        <TableCell style={{ width: '30%' }}>Section</TableCell>
                        <TableCell style={{ width: '30%' }}>Filename</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <audio controls>
                            <source
                              src={`${process.env.REACT_APP_SERVER_API}/videos/${rowData.doorOpen}/${_id}`}
                              type="video/mp4"
                            />
                          </audio>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          Door opens
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {rowData.doorOpen}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {rowData.doorClose && rowData.doorClose !== '' && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '30%' }}>Audio</TableCell>
                        <TableCell style={{ width: '30%' }}>Section</TableCell>
                        <TableCell style={{ width: '30%' }}>Filename</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <audio controls>
                            <source
                              src={`${process.env.REACT_APP_SERVER_API}/videos/${rowData.doorClose}/${_id}`}
                              type="video/mp4"
                            />
                          </audio>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          After door closes
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {rowData.doorClose}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            );
          }}
          options={{
            actionsColumnIndex: -1,
            pageSize: parseInt(pagination),
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
        <Box sx={{ mt: 2 }}>
          <BackButton />
        </Box>
      </Box>

      <AddAudio
        show={show}
        onHide={() => setShow(false)}
        setReflesh={setReflesh}
        _id={_id}
        user={user}
      />
    </ModernPageContainer>
  );
}
